import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, model, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FieldType } from "@ngx-formly/core";
import { TranslatePipe } from '@ngx-translate/core';
import { PhxInputSerialNumberWithWarehouseComponent } from '../../../shared/components/phx-input-serial-number-with-warehouse/phx-input-serial-number-with-warehouse.component';
import { Product } from "../../../generated/generated-types";
import { SearchResult } from "../../../shared/components/phx-product-in-stock-icon/phx-product-in-stock-icon.component";
import { SerialData } from "../../warehouse/components/storage-place-outbound-serial-list-widget/storage-place-outbound-serial-list-widget.component";

// type Props = FormlyFieldProps & { [additionalProperties: string]: any; } & {
//   storagePlaceId?: string;
//   productId?: string;
// };

@Component({
  selector: 'app-formly-serial-list',
  templateUrl: './formly-serial-list.component.html',
  styleUrl: './formly-serial-list.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PhxInputSerialNumberWithWarehouseComponent,
    TranslatePipe,
    CommonModule
  ]
})
export class FormlySerialListComponent extends FieldType {
  destroyRef = inject(DestroyRef);

  storagePlaceId = model<string>();
  product = model<Product>();

  disabled = model<boolean>();

  serials = model<SerialData[]>([{ serialNumber: "", validSerialNumber: null }]);

  /** reverse = true -> checks if serial number is available (not already in stock), reverse = false -> checks if serial number is in stock/the storage place */
  reverse = model<boolean>(true);

  validSerials = () => this.serials()?.filter(x => x.validSerialNumber && !x.serialError);
  // validSerials = computed(() => this.serials()?.filter(x => x.validSerialNumber && !x.serialError));

  ngOnInit() {
    if (this.model.originStoragePlace?.id || this.model.originStoragePlaceId) {
      this.storagePlaceId.set(this.model.originStoragePlace?.id ?? this.model.originStoragePlaceId);
      this.reverse.set(false);
    }
    else if (this.model.targetStoragePlace?.id || this.model.targetStoragePlaceId) {
      this.storagePlaceId.set(this.model.targetStoragePlace?.id ?? this.model.targetStoragePlaceId);
      this.reverse.set(true);
    }
    else
      this.disabled.set(true);
    if (this.model.product)
      this.product.set(this.model.product);

    this.field.options.fieldChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((x) => {
      switch (x.field.key) {
        case "originStoragePlace":
          this.storagePlaceId.set(x.value?.id);
          this.reverse.set(false);
          this.disabled.set(x.value?.id ? false : true);
          break;
        case "originStoragePlaceId":
          this.storagePlaceId.set(x.value);
          this.reverse.set(false);
          this.disabled.set(x.value ? false : true);
          break;
        case "targetStoragePlace":
          if (!this.model.originStoragePlace?.id && !this.model.originStoragePlaceId) {
            this.storagePlaceId.set(x.value?.id);
            this.reverse.set(true);
            this.disabled.set(x.value?.id ? false : true);
          }
          break;
        case "targetStoragePlaceId":
          if (!this.model.originStoragePlace?.id && !this.model.originStoragePlaceId) {
            if (typeof x.value != "string" && x.value.id)
              this.storagePlaceId.set(x.value.id);
            else
              this.storagePlaceId.set(x.value);
            this.reverse.set(true);
            this.disabled.set(this.storagePlaceId() ? false : true);
          }
          break;
        case "product":
          this.product.set(x.value);
          break;
        default:
          break;
      }
    });
    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
      if (x[0]) {
        this.form.markAsPristine();
      }
    });

    //setting formState.serialListControl in order to call reset() (e.g. in MobileManualStockTransactionWidgetComponent)
    this.formState.serialListControl = this;
  }

  onFocus(serialData: SerialData) {
    if ((serialData.serialNumber ?? "") == "" && this.serials().filter(x => (x.serialNumber ?? "").length == 0).length == 1)
      this.serials.update(x => { x.push({ serialNumber: "", validSerialNumber: null }); return x; });
  }

  onBlur(serialData: SerialData) {
    if ((serialData.serialNumber ?? "") == "" && this.serials().filter(x => (x.serialNumber ?? "").length == 0).length > 1) {
      this.serials.update(x => { x.splice(this.serials().indexOf(serialData), 1); return x; });
      if (serialData.validSerialNumber) {
        let result = this.validSerials();
        this.formControl.setValue(result);
      }
    }
  }

  onTextChanged(serialData: SerialData, _index: number, text: string) {
    if (serialData.validSerialNumber && ((text ?? "") == "" || text != serialData.validSerialNumber)) {
      serialData.validSerialNumber = null;
      let result = this.validSerials();
      this.formControl.setValue(result);
    }
    this.checkForErrors();
  }

  onValidSerialChanged(serialData: SerialData, _index: number, data: SearchResult) {
    serialData.validSerialNumber = data?.searchModel?.serialOrLotNumber;
    let result = this.validSerials();
    this.formControl.setValue(result);
  }

  checkForErrors() {
    if (!this.serials() || this.serials().length == 0)
      return;

    let errorSerials = this.serials().map(x => x.serialNumber)?.filter((v, i, a) => v.length > 0 && a.indexOf(v) != i) ?? [];

    let nonErrorItems = this.serials().filter(x => x.serialError && !errorSerials.includes(x.serialNumber));
    for (let nonErrorItem of nonErrorItems) {
      nonErrorItem.serialError = "";
    }

    let errorItems = this.serials().filter(x => !x.serialError && errorSerials.includes(x.serialNumber));
    for (let errorItem of errorItems) {
      errorItem.serialError = "serial-already-exists";
    }
  }

  reset() {
    this.serials.set([{ serialNumber: "", validSerialNumber: null }]);
  }
}
